import { useEffect, useState, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCTA } from "../../context/CTAContext";

// Modular components
import RatingBreakdown from "../../components/client-side/casinoreviews/RatingBreakdown";
import QuickFacts from "../../components/client-side/casinoreviews/QuickFacts";
import SignupOffer from "../../components/client-side/casinoreviews/SignupOfferCTA";
import ProsCons from "../../components/client-side/casinoreviews/ProsCons";
import BankingPayments from "../../components/client-side/casinoreviews/BankingPayments";
import GamesSoftware from "../../components/client-side/casinoreviews/GamesSoftware";
import CustomerPlayerSecurity from "../../components/client-side/casinoreviews/CustomerPlayerSecurity";
import CasinoContent from "../../components/client-side/casinoreviews/CasinoContent";
import FaqsSection from "../../components/client-side/casinoreviews/FaqsSection";
import { RegionContext } from "../../context/RegionContext";
import { getCasinoContent } from "../../controllers/reviewController";
import { getCasinoById } from "../../controllers/homeController";
import Loder from "../../components/client-side/global/loder";

const CasinoReviewPage = () => {
  const location = useLocation();
  const { casino } = location.state || {};
  const { selectedRegionId } = useContext(RegionContext);
  const [allPaymentMethods, setAllPaymentMethods] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minDeposit, setMinDeposit] = useState("N/A");
  const [welcomeOffer, setWelcomeOffer] = useState("N/A");
  const [casinoUrl, setCasinoUrl] = useState("#");
  const [casinoDetails, setCasinoDetails] = useState({});
  const [content, setContent] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const CTARef = useRef(null);

  const { setCTAHeight } = useCTA();

  useEffect(() => {
    if (CTARef.current) {
      const h =
        CTARef.current.offsetHeight ||
        CTARef.current.getBoundingClientRect().height ||
        0;
      setCTAHeight(h);
    }

    return () => {
      setCTAHeight(0);
    };
  }, [setCTAHeight]);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getCasinoDetails = async () => {
    try {
      const [contentData, casinoData] = await Promise.all([
        getCasinoContent(casino.id),
        getCasinoById(casino.id),
      ]);

      setCasinoDetails(casinoData);
      setAllPaymentMethods(casinoData.PaymentMethods || []);
      setContent(contentData);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching casino details:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    getCasinoDetails();
    // eslint-disable-next-line
  }, [casino?.id]);

  useEffect(() => {
    if (!allPaymentMethods.length) {
      setPaymentMethods([]);
      return;
    }

    if (!selectedRegionId) {
      setPaymentMethods(allPaymentMethods);
      return;
    }

    const filtered = allPaymentMethods.filter((pm) => {
      return (
        pm.CasinoRegionPayment &&
        String(pm.CasinoRegionPayment.regionId) === String(selectedRegionId)
      );
    });
    if (filtered.length > 0) {
      const firstPayment = filtered[0].CasinoRegionPayment;
      setMinDeposit(firstPayment.minDeposit || "N/A");
      const welcomeOffer = casino?.freeSpins
        ? firstPayment.welcomeOffer +
            " + " +
            (casino.freeSpins || "") +
            " Free Spins" || "N/A"
        : firstPayment.welcomeOffer;
      setWelcomeOffer(welcomeOffer);
      setCasinoUrl(firstPayment.casinoUrl || "#");
    } else {
      setMinDeposit("N/A");
      setWelcomeOffer("N/A");
      setCasinoUrl("#");
    }
    setPaymentMethods(filtered);
    // eslint-disable-next-line
  }, [allPaymentMethods, selectedRegionId]);

  const quickFacts = [
    {
      label: "Established",
      value: casinoDetails?.established || "N/A",
    },
    { label: "License", value: casinoDetails?.licensed || "N/A" },
    { label: "Min. Deposit", value: minDeposit || "N/A" },
    { label: "Withdrawal Time", value: casinoDetails?.payoutSpeed || "N/A" },
    { label: "Live Chat", value: casinoDetails?.liveChat || "N/A" },
    { label: "Owned by", value: casinoDetails?.ownedBy || "N/A" },
  ];

  const signupOfferData = {
    title: welcomeOffer,
    buttonText: "Sign Up Now",
    link: casinoUrl,
    logoUrl: casinoDetails?.image,
    description: casinoDetails?.description,
    promoCode: casinoDetails?.promoCode || "",
  };

  if (loading) return <Loder />;

  return (
    <main className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100 transition-colors duration-300">
      {/* Page Header */}
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20 pt-10 pb-4">
        <header className="text-left">
          <h1 className="text-3xl font-bold capitalize text-gray-900 dark:text-gray-100">
            {`${casinoDetails?.name || "Casino"} Review`}
          </h1>
        </header>
      </div>

      {/* Modular sections */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20">
        <div className="flex flex-col lg:flex-row gap-10 w-full items-stretch">
          {/* Rating Breakdown */}
          {casinoDetails?.ratings && (
            <div className="lg:w-[70%] w-full">
              <RatingBreakdown
                data={casinoDetails?.ratings}
                overAllRating={casinoDetails.rating}
                image={casinoDetails?.image}
              />
            </div>
          )}

          {/* Quick Facts */}
          {quickFacts && (
            <aside className="lg:w-[30%] w-full">
              <QuickFacts facts={quickFacts} />
            </aside>
          )}
        </div>
      </section>

      {/* CTA */}

      {signupOfferData && (
        <div
          ref={CTARef}
          className={`${
            scrolled
              ? "fixed bottom-0 !w-screen z-50  "
              : " max-w-[1440px] mx-auto px-5 sm:px-8 md:px-12 lg:px-16 xl:px-20"
          }`}
        >
          <SignupOffer data={signupOfferData} scrolled={scrolled} />
        </div>
      )}

      {/* Pros / Cons */}
      {casinoDetails?.prosCons && (
        <ProsCons data={casinoDetails?.prosCons} name={casinoDetails?.name} />
      )}

      {/* Banking & Payments */}
      {paymentMethods?.length > 0 && (
        <BankingPayments
          methods={paymentMethods}
          minDeposit={minDeposit}
          name={casinoDetails?.name}
        />
      )}

      {/* Games & Software */}
      {casinoDetails?.gameCategories && (
        <GamesSoftware
          games={casinoDetails?.gameCategories}
          softwareProviders={casinoDetails?.softwareProvided}
          name={casinoDetails?.name}
        />
      )}

      {/* Support & Security */}
      {casinoDetails?.casinoSupports && (
        <CustomerPlayerSecurity
          support={casinoDetails?.casinoSupports}
          licensing={casinoDetails?.licensing}
          supportedLanguages={casinoDetails?.supportedLanguages}
          name={casinoDetails?.name}
        />
      )}

      {/* Casino Content */}
      {content && content?.isPublished && <CasinoContent content={content} />}

      {/* FAQs */}
      <FaqsSection name={casinoDetails?.name} />
    </main>
  );
};

export default CasinoReviewPage;
